import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useBoard } from '@/hooks/useBoard';
import { useI18n } from '@/hooks/useI18n';
import { ApiError } from '@/lib/api/client';
import { classroomApi } from '@/lib/api/endpoints';
import type { LessonSession } from '@/lib/api/types';
import { routes } from '@/lib/routes';

/** Minimal active lesson shell — CLASSROOM_ONLY, no private cabinet routes. */
const ClassroomSessionPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isActive } = useBoard();
  const [session, setSession] = useState<LessonSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = params.get('id');

  useEffect(() => {
    if (!isActive || !sessionId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const next = await classroomApi.readSession(sessionId);
        if (!cancelled) setSession(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('common.error'));
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isActive, sessionId, t]);

  if (!isActive) return <Navigate to={routes.classroomEntry} replace />;
  if (!sessionId) return <Navigate to={routes.classroomToday} replace />;

  const endLesson = async () => {
    if (!sessionId) return;
    try {
      await classroomApi.endSession(sessionId);
      navigate(routes.classroomToday);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  return (
    <div className="classroom-shell">
      <header className="classroom-shell-header">
        <div>
          <p className="classroom-shell-eyebrow">{t('auth.classroom.title')}</p>
          <h1>{session?.course_title ?? t('common.loading')}</h1>
          {session && (
            <p>
              {session.stage} · {session.group_codes.join(', ')} · {session.room}
            </p>
          )}
        </div>
        <div className="classroom-shell-actions">
          <button type="button" className="auth-secondary" onClick={() => navigate(routes.classroomToday)}>
            {t('auth.classroom.backToday')}
          </button>
          <button type="button" className="auth-primary" onClick={() => void endLesson()}>
            {t('auth.classroom.endLesson')}
          </button>
        </div>
      </header>
      {error && <p className="field-error">{error}</p>}
      <p className="auth-note">{t('auth.classroom.sessionNote')}</p>
    </div>
  );
};

export default ClassroomSessionPage;
